import _ from "lodash";
import { initWdyr } from "./Utils/wdyr";
import { initSentry, Sentry } from "./services/sentry";
import "regenerator-runtime/runtime.js"; // needed for polyfilling in react-mic library. TODO: remove this once checked react-mic package
import "@toddle-edu/ds-web/dist/assets/antd.css";
import "@toddle-edu/ds-web/dist/assets/main.css";
import "@toddle-edu/doc-editor/dist/main.css";
import "@toddle-edu/ds-data-grid/dist/main.css";
import "@toddle-edu/ds-doc-editor/dist/main.css";
import classes from "./main.scss";
import { logPageView } from "Utils";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { routes, plainRoutes } from "store/routes";
import { store, browserHistory as history } from "./store/createStore";
import { initAllThirdPartyModules } from "Login/modules/LoginModule/LoginUtils";
import { setUserInfoInStorage } from "./services/localStorage";
import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";

import AppContainer from "./containers/AppContainer";
import { ApolloProvider } from "@apollo/client";
import client from "lib/apolloClient/apolloClientWrapper";
import { setupNavigationInterception } from "lib/externalLinkInterceptor";

import { enableLogPageView } from "config";
initAllThirdPartyModules();
initWdyr();

history.listen((location, action) => {
  if (enableLogPageView) {
    logPageView(location);
  }
  if (window.Intercom) {
    window.Intercom("update");
  }
});

/**
 * Handle pending org switch for AI Playground import in new tab.
 * This runs before the app loads to switch to the target org.
 * Reads org switch data from localStorage and sets up auth for the new org.
 */
const handlePendingAIPlaygroundOrgSwitch = () => {
  const pendingOrgSwitch = localStorage.getItem(
    "aiPlayground_pendingOrgSwitch"
  );
  if (pendingOrgSwitch) {
    try {
      const orgSwitchData = JSON.parse(pendingOrgSwitch);
      localStorage.removeItem("aiPlayground_pendingOrgSwitch");

      // Map user type to entity type
      const USER_TYPE_ENTITY_MAP = {
        staff: "Staff",
        student: "Student",
        parent: "Parent",
      };

      const userType = _.get(orgSwitchData, "type", "");

      // Build user info for storage (similar to onSwitchToOrganizationLocally)
      const userInfo = {
        id: _.get(orgSwitchData, "id", ""),
        org_id: _.get(orgSwitchData, "organization.id", ""),
        identityId: _.get(orgSwitchData, "identityId", ""),
        token: _.get(orgSwitchData, "jwt", ""),
        user_type: userType,
        userEntityType: _.get(USER_TYPE_ENTITY_MAP, userType, ""),
      };

      // Set the user info in storage (this switches the auth)
      setUserInfoInStorage(userInfo);

      // Set pending redirect to AI Playground
      localStorage.setItem("pendingRedirectPath", "ai-playground");
    } catch (error) {
      console.error("Error handling pending AI Playground org switch:", error);
      localStorage.removeItem("aiPlayground_pendingOrgSwitch");
    }
  }
};
handlePendingAIPlaygroundOrgSwitch();

const checkPendingRedirect = () => {
  const pendingRedirectPath = localStorage.getItem("pendingRedirectPath");
  if (pendingRedirectPath) {
    localStorage.removeItem("pendingRedirectPath");
    const checkInterval = setInterval(() => {
      const currentPath = window.location.pathname;
      const platformMatch = currentPath.match(/^\/platform\/(\d+)(\/|$)/);
      if (platformMatch) {
        clearInterval(checkInterval);
        const orgId = platformMatch[1];
        const newPath = `/platform/${orgId}/${pendingRedirectPath}`;
        if (!currentPath.includes(pendingRedirectPath)) {
          window.location.href = newPath;
        }
      }
    }, 300);

    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
  }
};
checkPendingRedirect();

// ========================================================
// Developer Tools Setup
// ========================================================
// if (__DEBUG__) {
//   if (window.devToolsExtension) {
//     window.devToolsExtension.open();
//   }
// }

// ========================================================
// Render Setup
// ========================================================
const MOUNT_NODE = document.getElementById("root");

initSentry({ plainRoutes });

//written to disable react-beautiful-dnd warnings.
window["__react-beautiful-dnd-disable-dev-warnings"] = true;

const Main = () => {
  return (
    <Suspense
      fallback={
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      }
    >
      <ApolloProvider client={client}>
        <AppContainer
          store={store}
          history={history}
          routes={routes}
          // routerKey={routerKey}
        />
      </ApolloProvider>
    </Suspense>
  );
};

setupNavigationInterception();

const App = Sentry.withProfiler(Main);
const render = () => {
  const root = createRoot(MOUNT_NODE);
  root.render(<App />);
};

// Enable HMR and catch runtime errors in RedBox
// This code is excluded from production bundle
// if (__DEV__ && module.hot) {
//   const renderApp = render;
//   const renderError = error => {
//     const RedBox = require("redbox-react").default;

//     ReactDOM.render(<RedBox error={error} />, MOUNT_NODE);
//   };
//   render = () => {
//     try {
//       renderApp(Math.random());
//     } catch (error) {
//       renderError(error);
//     }
//   };
//   module.hot.accept(["./routes/index"], () => render());
// }

// ========================================================
// Go!
// ========================================================
render();
